import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: 'Clock' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: 'Send' },
  opened: { label: 'Opened', color: 'bg-purple-100 text-purple-700', icon: 'Eye' },
  reviewed: { label: 'Reviewed', color: 'bg-green-100 text-green-700', icon: 'Star' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: 'AlertCircle' },
};

const ReviewRequests = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [googleUrl, setGoogleUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', channel: 'email', notes: '' });
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const { profile, organization, updateOrganization } = useAuth();

  const stats = {
    total: requests?.length,
    sent: requests?.filter(r => r?.status === 'sent' || r?.status === 'opened' || r?.status === 'reviewed')?.length,
    reviewed: requests?.filter(r => r?.status === 'reviewed')?.length,
    conversionRate: requests?.length > 0 ? Math.round((requests?.filter(r => r?.status === 'reviewed')?.length / requests?.length) * 100) : 0,
  };

  const fetchRequests = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    const { data } = await supabase?.from('review_requests')?.select('*')?.eq('org_id', profile?.org_id)?.order('created_at', { ascending: false });
    setRequests(data || []);
    setLoading(false);
  }, [profile?.org_id]);

  useEffect(() => {
    fetchRequests();
    if (organization?.google_business_url) setGoogleUrl(organization?.google_business_url);
  }, [fetchRequests, organization?.google_business_url]);

  const handleSaveGoogleUrl = async () => {
    setSavingUrl(true);
    await updateOrganization({ google_business_url: googleUrl });
    setSavingUrl(false);
    setSuccessMsg('Google Business URL saved!');
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSendRequest = async () => {
    if (!form?.customer_name) { setErrorMsg('Customer name is required'); return; }
    if (form?.channel === 'email' && !form?.customer_email) { setErrorMsg('Email is required for email channel'); return; }
    if (form?.channel === 'sms' && !form?.customer_phone) { setErrorMsg('Phone is required for SMS channel'); return; }
    setSending(true);
    setErrorMsg('');
    try {
      const reviewLink = organization?.google_business_url || 'https://g.page/r/review';
      const { error } = await supabase?.from('review_requests')?.insert({
        org_id: profile?.org_id,
        customer_name: form?.customer_name,
        customer_email: form?.customer_email || null,
        customer_phone: form?.customer_phone || null,
        channel: form?.channel,
        status: 'sent',
        review_link: reviewLink,
        sent_at: new Date()?.toISOString(),
        notes: form?.notes || null,
      });
      if (error) throw error;

      // Insert notification
      await supabase?.from('notifications')?.insert({
        org_id: profile?.org_id,
        type: 'review_request',
        title: 'Review Request Sent',
        body: `Review request sent to ${form?.customer_name} via ${form?.channel}`,
        is_read: false,
      });

      setSuccessMsg(`Review request sent to ${form?.customer_name}!`);
      setForm({ customer_name: '', customer_email: '', customer_phone: '', channel: 'email', notes: '' });
      setShowModal(false);
      fetchRequests();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setErrorMsg(err?.message || 'Failed to send review request');
    } finally {
      setSending(false);
    }
  };

  const handleSendReminder = async (req) => {
    await supabase?.from('review_requests')?.update({ reminder_sent_at: new Date()?.toISOString(), status: 'sent' })?.eq('id', req?.id);
    setSuccessMsg(`Reminder sent to ${req?.customer_name}`);
    fetchRequests();
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleMarkReviewed = async (id) => {
    await supabase?.from('review_requests')?.update({ status: 'reviewed', reviewed_at: new Date()?.toISOString() })?.eq('id', id);
    fetchRequests();
  };

  return (
    <>
      <Helmet>
        <title>Review Requests - Wilcart CRM</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-8">
            {/* Page Header */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Review Requests</h1>
                  <p className="text-muted-foreground mt-1">Automate Google review requests after job completion</p>
                </div>
                <Button onClick={() => setShowModal(true)} iconName="Plus" iconPosition="left">
                  New Review Request
                </Button>
              </div>
            </motion.div>

            {successMsg && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">{successMsg}</div>}
            {errorMsg && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{errorMsg}</div>}

            {/* Google Business Setup */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="bg-card border border-border rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Icon name="Star" size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h2 className="font-semibold text-card-foreground">Google Business Profile</h2>
                  <p className="text-sm text-muted-foreground">Connect your Google Business review link</p>
                </div>
              </div>
              <div className="flex gap-3">
                <input
                  type="url"
                  value={googleUrl}
                  onChange={e => setGoogleUrl(e?.target?.value)}
                  placeholder="https://g.page/r/your-business/review"
                  className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button onClick={handleSaveGoogleUrl} disabled={savingUrl} variant="outline">
                  {savingUrl ? 'Saving...' : 'Save'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Find your review link in Google Business Profile → Get more reviews → Share review form
              </p>
            </motion.div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Requests', value: stats?.total, icon: 'Send', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
                { label: 'Sent', value: stats?.sent, icon: 'CheckCircle', color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
                { label: 'Reviews Received', value: stats?.reviewed, icon: 'Star', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400' },
                { label: 'Conversion Rate', value: `${stats?.conversionRate}%`, icon: 'TrendingUp', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' },
              ]?.map(stat => (
                <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat?.color}`}>
                      <Icon name={stat?.icon} size={18} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-card-foreground">{stat?.value}</p>
                      <p className="text-xs text-muted-foreground">{stat?.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Requests Table */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h2 className="font-semibold text-card-foreground">Review Request History</h2>
              </div>
              {loading ? (
                <div className="p-6 space-y-3">
                  {[1,2,3]?.map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}
                </div>
              ) : requests?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                    <Icon name="Star" size={28} className="text-muted-foreground" />
                  </div>
                  <p className="font-medium text-foreground">No review requests yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Send your first review request to a customer</p>
                  <Button onClick={() => setShowModal(true)} className="mt-4" iconName="Plus" iconPosition="left">
                    Send First Request
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Channel</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Sent</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Reminder</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests?.map(req => {
                        const cfg = STATUS_CONFIG?.[req?.status] || STATUS_CONFIG?.pending;
                        return (
                          <tr key={req?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                            <td className="py-3 px-4">
                              <p className="font-medium text-card-foreground text-sm">{req?.customer_name}</p>
                              <p className="text-xs text-muted-foreground">{req?.customer_email || req?.customer_phone || '—'}</p>
                            </td>
                            <td className="py-3 px-4">
                              <span className="flex items-center space-x-1 text-sm text-muted-foreground">
                                <Icon name={req?.channel === 'sms' ? 'MessageSquare' : 'Mail'} size={14} />
                                <span className="capitalize">{req?.channel}</span>
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${cfg?.color}`}>
                                <Icon name={cfg?.icon} size={11} />
                                <span>{cfg?.label}</span>
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {req?.sent_at ? new Date(req?.sent_at)?.toLocaleDateString() : '—'}
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {req?.reminder_sent_at ? new Date(req?.reminder_sent_at)?.toLocaleDateString() : '—'}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end space-x-2">
                                {req?.status !== 'reviewed' && (
                                  <button onClick={() => handleSendReminder(req)} className="text-xs text-primary hover:underline">
                                    Remind
                                  </button>
                                )}
                                {req?.status !== 'reviewed' && (
                                  <button onClick={() => handleMarkReviewed(req?.id)} className="text-xs text-green-600 hover:underline">
                                    Mark Reviewed
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      {/* New Request Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-card border border-border rounded-xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-card-foreground">Send Review Request</h2>
              <button onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>
            {errorMsg && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{errorMsg}</div>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-1">Customer Name *</label>
                <input type="text" value={form?.customer_name} onChange={e => setForm(p => ({...p, customer_name: e?.target?.value}))}
                  placeholder="John Smith" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-1">Channel</label>
                <div className="flex space-x-3">
                  {['email', 'sms']?.map(ch => (
                    <button key={ch} onClick={() => setForm(p => ({...p, channel: ch}))}
                      className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${form?.channel === ch ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary'}`}>
                      <Icon name={ch === 'sms' ? 'MessageSquare' : 'Mail'} size={14} className="inline mr-1" />
                      {ch === 'sms' ? 'SMS' : 'Email'}
                    </button>
                  ))}
                </div>
              </div>
              {form?.channel === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-1">Email Address *</label>
                  <input type="email" value={form?.customer_email} onChange={e => setForm(p => ({...p, customer_email: e?.target?.value}))}
                    placeholder="customer@email.com" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              )}
              {form?.channel === 'sms' && (
                <div>
                  <label className="block text-sm font-medium text-card-foreground mb-1">Phone Number *</label>
                  <input type="tel" value={form?.customer_phone} onChange={e => setForm(p => ({...p, customer_phone: e?.target?.value}))}
                    placeholder="+1 (555) 000-0000" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-1">Notes (optional)</label>
                <textarea value={form?.notes} onChange={e => setForm(p => ({...p, notes: e?.target?.value}))}
                  placeholder="Job completed, customer was happy..." rows={2}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
              </div>
              <div className="flex space-x-3 pt-2">
                <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSendRequest} disabled={sending} className="flex-1" iconName="Send" iconPosition="left">
                  {sending ? 'Sending...' : 'Send Request'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default ReviewRequests;
